<div class="flex flex-col flex-1 px-3 py-2 max-w-5xl mx-auto w-full">
  <div class="card border border-gray-200 rounded-lg mb-4">
    <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
      <h2 class="text-sm font-semibold">GitHub integration</h2>
      @if (github) {
        @if (github.connected) {
          <span class="badge badge-sm badge-success">connected</span>
        } @else if (github.auth_method !== 'none' && !github.repo) {
          <span class="badge badge-sm badge-warning">token set · no repo</span>
        } @else {
          <span class="badge badge-sm badge-ghost">not connected</span>
        }
      }
    </div>

    <div class="p-4">
      <p class="text-sm text-gray-500 mb-4">
        Paste a GitHub access token, then pick the repository issues are filed into. Once connected,
        any admin gets a
        <b>Create GH Issue</b>
        button on a generated AI Ticket. Use a
        <b>fine-grained Personal Access Token</b>
        scoped to just the target repository with
        <code>Issues: write</code>
        — that keeps the blast radius minimal. The token is stored server-side and never shown
        again.
      </p>

      <!-- 1. Connected via a pasted token → repo picker -->
      @if (connectedViaToken) {
        <div class="flex items-center gap-2 mb-4 text-sm">
          <i class="ri-github-fill text-lg"></i>
          <span>
            Token for
            <b>{{ '@' + github!.github_login }}</b>
          </span>
          <button
            class="btn btn-xs btn-ghost ml-2"
            (click)="showTokenForm = !showTokenForm"
            [disabled]="githubSaving"
          >
            Replace token
          </button>
          <button class="btn btn-xs btn-ghost" (click)="disconnect()" [disabled]="githubSaving">
            Disconnect
          </button>
        </div>

        @if (showTokenForm) {
          <div class="flex items-end gap-3 mb-4">
            <div class="flex-1">
              <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
                New token
              </label>
              <input
                type="password"
                autocomplete="off"
                placeholder="github_pat_…"
                class="input input-sm input-bordered w-full"
                [(ngModel)]="tokenInput"
              />
            </div>
            <button
              class="btn btn-sm btn-primary"
              (click)="saveToken()"
              [disabled]="tokenSaving || !tokenInput.trim()"
            >
              @if (tokenSaving) {
                <span class="loading loading-spinner loading-xs"></span>
              }
              Validate &amp; save
            </button>
          </div>
          <p class="text-xs text-gray-500 -mt-2 mb-4">
            Get one:
            <a
              [href]="fineGrainedTokenUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="link link-primary"
            >
              fine-grained ↗
            </a>
            (Issues: Read &amp; write on
            <b>{{ selectedRepo.trim() || github!.repo || 'the repo' }}</b>
            ) or
            <a [href]="classicTokenUrl" target="_blank" rel="noopener noreferrer" class="link">
              classic ↗
            </a>
            (prefilled).
          </p>
        }

        <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
          Target repository
        </label>
        <input
          type="text"
          placeholder="Filter repositories…"
          class="input input-sm input-bordered w-full mb-2"
          [(ngModel)]="repoFilter"
        />
        @if (loadingRepos) {
          <p class="text-xs text-gray-500">
            <span class="loading loading-spinner loading-xs"></span>
            Loading repositories…
          </p>
        } @else {
          <select
            class="select select-sm select-bordered w-full"
            [(ngModel)]="selectedRepo"
            size="8"
          >
            @for (r of filteredRepos; track r.full_name) {
              <option [value]="r.full_name">
                {{ r.full_name }}{{ r.private ? ' (private)' : '' }}
              </option>
            }
          </select>
          <p class="text-xs text-gray-500 mt-1">
            {{ filteredRepos.length }} of {{ repos.length }} repositories this token can push to.
          </p>
        }

        <div class="flex items-center gap-3 mt-4">
          <button
            class="btn btn-sm btn-primary"
            (click)="saveRepo(selectedRepo)"
            [disabled]="githubSaving || !selectedRepo"
          >
            @if (githubSaving) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Save repository
          </button>
          @if (github!.repo) {
            <button class="btn btn-sm btn-ghost" (click)="saveRepo('')" [disabled]="githubSaving">
              Clear repo
            </button>
          }
          @if (githubSaved) {
            <span class="text-xs text-green-700">✓ Saved.</span>
          }
        </div>
      }

      <!-- 2. Legacy env-PAT path (no pasted token) → manual owner/repo -->
      @else if (github && github.auth_method === 'env') {
        <p class="text-xs text-gray-500 border-l-2 border-gray-200 pl-3 mb-4">
          ✓ Using the legacy
          <code>TRACER_GITHUB_TOKEN</code>
          env token. Paste a token below to manage this from the UI instead (with a repo picker).
        </p>
        <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
          Target repository (owner/repo)
        </label>
        <input
          type="text"
          placeholder="AirGateway/some-repo"
          class="input input-sm input-bordered w-full"
          [(ngModel)]="selectedRepo"
        />
        <div class="flex items-center gap-3 mt-4">
          <button
            class="btn btn-sm btn-primary"
            (click)="saveRepo(selectedRepo)"
            [disabled]="githubSaving"
          >
            @if (githubSaving) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Save repository
          </button>
          @if (githubSaved) {
            <span class="text-xs text-green-700">✓ Saved.</span>
          }
        </div>

        <div class="divider text-xs text-gray-400">or paste a token</div>
        <p class="text-xs text-gray-500 mb-2">
          Need one?
          <a
            [href]="fineGrainedTokenUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="link link-primary"
          >
            fine-grained token ↗
          </a>
          (Issues: Read &amp; write on
          <b>{{ selectedRepo.trim() || 'your repo' }}</b>
          ) or
          <a [href]="classicTokenUrl" target="_blank" rel="noopener noreferrer" class="link">
            classic token ↗
          </a>
          (prefilled).
        </p>
        <div class="flex items-end gap-3">
          <div class="flex-1">
            <input
              type="password"
              autocomplete="off"
              placeholder="github_pat_…"
              class="input input-sm input-bordered w-full"
              [(ngModel)]="tokenInput"
            />
          </div>
          <button
            class="btn btn-sm btn-outline"
            (click)="saveToken()"
            [disabled]="tokenSaving || !tokenInput.trim()"
          >
            @if (tokenSaving) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Validate &amp; save
          </button>
        </div>
      }

      <!-- 3. Not connected → name the repo, grab a token, paste it -->
      @else if (github) {
        <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
          Target repository (owner/repo)
        </label>
        <input
          type="text"
          placeholder="AirGateway/some-repo"
          class="input input-sm input-bordered w-full"
          [(ngModel)]="selectedRepo"
        />

        <div
          class="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3 mt-2 mb-4"
        >
          <div class="font-semibold mb-1">Need a token?</div>
          <a
            [href]="fineGrainedTokenUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="link link-primary"
          >
            Create a fine-grained token on GitHub ↗
          </a>
          <span class="text-gray-500">(recommended — least privilege)</span>
          <ul class="list-disc ml-5 mt-1 text-gray-500">
            <li>
              Repository access →
              <b>Only select repositories</b>
              →
              <b>{{ selectedRepo.trim() || 'your repo' }}</b>
            </li>
            <li>
              Permissions →
              <b>Issues: Read and write</b>
              (Metadata: Read-only is added automatically)
            </li>
          </ul>
          <div class="mt-2">
            or
            <a [href]="classicTokenUrl" target="_blank" rel="noopener noreferrer" class="link">
              create a classic token ↗
            </a>
            <span class="text-gray-500">— opens prefilled with scope &amp; description.</span>
          </div>
        </div>

        <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">
          GitHub access token
        </label>
        <div class="flex items-end gap-3">
          <div class="flex-1">
            <input
              type="password"
              autocomplete="off"
              placeholder="github_pat_…"
              class="input input-sm input-bordered w-full"
              [(ngModel)]="tokenInput"
            />
          </div>
          <button
            class="btn btn-sm btn-primary"
            (click)="saveToken()"
            [disabled]="tokenSaving || !tokenInput.trim()"
          >
            @if (tokenSaving) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Validate &amp; save
          </button>
        </div>
        <p class="text-xs text-gray-500 mt-2">
          We validate the token against GitHub before saving and never display it again.
        </p>
      }

      @if (githubError) {
        <div class="alert alert-error text-sm mt-3">{{ githubError }}</div>
      }
    </div>
  </div>
</div>
